import { ChatMessage, ProposalState, ProposalStatus } from '../models/clinicAgent';

// Raw row shape of ChatSerializer: GET /api/chat/ and, with conversationId,
// GET /api/clinic/agent/conversations/{id}/messages/.
export interface ChatHistoryRow {
  id: number;
  message: string;
  role: 'user' | 'assistant';
  createdAt: string; // "dd-mm-yyyy HH:MM:SS"
  read: boolean;
  // Attachment: a uri-compatible string (hosted URL for history, or a base64
  // data URL) plus its MIME type. Images render as <img>; PDFs as a file chip.
  // fileName is present for PDFs (read snake_case too, in case the serializer
  // exposes it that way like message_type).
  fileData?: string;
  fileType?: string;
  fileName?: string;
  file_name?: string;
  // "text" (default) or "proposal" (a persisted proposal whose state lives in
  // metadata). Read both casings: existing fields arrive camelCase, but the
  // serializer may expose snake_case.
  messageType?: 'text' | 'proposal';
  message_type?: 'text' | 'proposal';
  trigger?: string;
  // For a proposal row: { proposal_id, run_id, action, patient_id, payload,
  // rationale, status, detail }.
  metadata?: Record<string, unknown>;
  conversationId?: number;
}

// Map a persisted proposal row's metadata onto a renderable card. Server status
// is pending | applied | discarded; the transient 'applying'/'error' states are
// local only, so anything unrecognised falls back to 'pending' (still openable).
function toProposalState(md: Record<string, unknown>): ProposalState {
  const raw = String(md.status ?? 'pending').toLowerCase();
  const status: ProposalStatus =
    raw === 'applied' ? 'applied' : raw === 'discarded' ? 'discarded' : 'pending';
  const runId = Number(md.run_id);
  return {
    id: typeof md.proposal_id === 'string' ? md.proposal_id : undefined,
    run_id: Number.isFinite(runId) ? runId : undefined,
    action: String(md.action ?? ''),
    patient_id: (md.patient_id as number | null | undefined) ?? null,
    payload: (md.payload as Record<string, unknown>) ?? {},
    rationale: String(md.rationale ?? ''),
    detail: (md.detail as Record<string, unknown>) ?? undefined,
    status,
  };
}

// Parse the backend's "dd-mm-yyyy HH:MM:SS" timestamp into epoch ms.
export function parseChatTimestamp(raw: string): number {
  const m = /^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/.exec(raw || '');
  if (!m) return Date.now();
  const [, dd, mm, yyyy, hh, min, ss] = m;
  return new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(min), Number(ss)).getTime();
}

export function chatRowToMessage(r: ChatHistoryRow): ChatMessage {
  const messageType = r.messageType ?? r.message_type ?? 'text';
  const base: ChatMessage = {
    id: `hist-${r.id}`,
    serverId: r.id,
    role: r.role,
    text: r.message ?? '',
    ts: parseChatTimestamp(r.createdAt),
    ...(r.fileData
      ? {
          fileUrl: r.fileData,
          fileType: r.fileType,
          ...(r.fileName ?? r.file_name ? { fileName: r.fileName ?? r.file_name } : {}),
        }
      : {}),
  };

  // A persisted proposal row: empty message, state lives in metadata. Render
  // as an interactive card (its status drives whether the buttons show).
  if (messageType === 'proposal' && r.metadata) {
    return {
      ...base,
      messageType: 'proposal',
      proposals: [toProposalState(r.metadata)],
    };
  }

  return base;
}
